import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { EidCard } from "@/components/prayer/EidCard";
import { JummahCard } from "@/components/prayer/JummahCard";
import { RamadanCard } from "@/components/prayer/RamadanCard";
import { TimetableList } from "@/components/prayer/TimetableList";
import { PageHeading } from "@/components/ui/PageHeading";
import { Section } from "@/components/ui/Section";
import { formatHijriLong } from "@/domain/hijri/formatHijri";
import { toHijri } from "@/domain/hijri/toHijri";
import { useDisplaySchedule } from "@/hooks/useDisplaySchedule";
import {
  useEidSettings,
  useHijriSettings,
  useJummahSettings,
} from "@/hooks/useSettings";
import { useNow } from "@/hooks/useNow";
import { cn } from "@/lib/cn";
import { addIstDays, formatIst, toIst } from "@/lib/time";

export function TimetablePage() {
  const now = useNow(60_000);
  const hijriSettings = useHijriSettings();
  const jummah = useJummahSettings();
  const eid = useEidSettings();
  const { today, tomorrow } = useDisplaySchedule(now);

  const [showTomorrow, setShowTomorrow] = useState(false);

  const todayHijri = toHijri(now, hijriSettings.offset);
  const tomorrowDate = addIstDays(now, 1);
  const tomorrowHijri = toHijri(tomorrowDate, hijriSettings.offset);
  const isFridayToday = toIst(now).getDay() === 5;
  const isRamadan = todayHijri.month === 9;
  const fajr = today.prayers.find((p) => p.name === "fajr")?.azaanTime;
  const maghrib = today.prayers.find((p) => p.name === "maghrib")?.azaanTime;

  return (
    <div>
      <PageHeading
        eyebrow="Salah"
        title="Prayer Timetable"
        subtitle="Times for Dariyapur. Iqamah times are set by the masjid committee."
      />

      <Section
        title={formatIst(now, "EEEE, d MMMM yyyy")}
        eyebrow={`Today · ${formatHijriLong(todayHijri)}`}
      >
        <TimetableList schedule={today} now={now} />
      </Section>

      <div className="space-y-4 pb-2">
        {fajr && maghrib ? (
          <RamadanCard fajr={fajr} maghrib={maghrib} isRamadan={isRamadan} />
        ) : null}
        <JummahCard settings={jummah} isToday={isFridayToday} />
        <EidCard settings={eid} />
      </div>

      <Section className="pb-8">
        <button
          type="button"
          onClick={() => setShowTomorrow((v) => !v)}
          aria-expanded={showTomorrow}
          aria-controls="tomorrow-timetable"
          className="flex w-full items-center justify-between rounded-card border border-line bg-cream/60 px-4 py-3 text-left transition-colors hover:bg-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-masjidGreen"
        >
          <div>
            <div className="label-eyebrow">Tomorrow</div>
            <div className="mt-0.5 font-serif text-base text-ink">
              {showTomorrow ? "Hide" : "View"} tomorrow&rsquo;s timetable
            </div>
            <div className="mt-0.5 text-xs text-muted">
              {formatIst(tomorrowDate, "EEEE, d MMMM yyyy")} · {formatHijriLong(tomorrowHijri)}
            </div>
          </div>
          <ChevronDown
            size={20}
            aria-hidden
            className={cn(
              "shrink-0 text-masjidGreen transition-transform",
              showTomorrow && "rotate-180",
            )}
          />
        </button>
        {showTomorrow ? (
          <div id="tomorrow-timetable" className="mt-3">
            {/* Epoch anchor: makes every prayer render as upcoming (no completed/current). */}
            <TimetableList schedule={tomorrow} now={new Date(0)} />
          </div>
        ) : null}
      </Section>
    </div>
  );
}
